import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  Download,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  TrendingUp,
  DollarSign,
  Building2,
  Globe,
  Banknote,
  PiggyBank,
  LineChart,
  BarChart3,
  ArrowLeftRight,
  List,
  Sparkles,
  PenTool
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { 
  economicsCurriculum, 
  economicsSchemeOfWork,
  economicsYearlyPlanner
} from '@/data/economicsCurriculum';
import { 
  year11EconomicsUnitTests,
  year12EconomicsUnitTests,
  year13EconomicsUnitTests,
  year11EconomicsExercises,
  year12EconomicsExercises,
  year13EconomicsExercises,
  economicsResources,
  economicsTestBlueprints,
  economicsAssessment
} from '@/data/economicsUnitTests';
import { economicsWeeklyLessons } from '@/data/economicsWeeklyLessons';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files } from 'lucide-react';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function EconomicsCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [expandedStrands, setExpandedStrands] = useState({});
  const [showAnswers, setShowAnswers] = useState({});
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  // Fetch teacher's assigned classes to get their year levels
  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        // Extract unique years from teacher's classes (Economics is Years 11-13)
        const years = [...new Set(classes
          .filter(c => c.year >= 11 && c.year <= 13 && 
            (c.subject?.toLowerCase().includes('economics') || 
             c.subject?.toLowerCase().includes('econ')))
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        setTeacherYears(years.length > 0 ? years : ["Year 11", "Year 12", "Year 13"]);
        // Auto-select first year if available
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        // Fallback to all years if API fails
        setTeacherYears(["Year 11", "Year 12", "Year 13"]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  // Strand icons based on Economics topics
  const strandIcons = {
    0: TrendingUp,    // Market System
    1: Building2,     // Public Sector
    2: Banknote,      // Money
    3: Globe          // Trade
  };

  const strandColors = ["blue", "green", "purple", "orange"];

  // Toggle show/hide answers for tests
  const toggleAnswers = (testId) => {
    setShowAnswers(prev => ({
      ...prev,
      [testId]: !prev[testId]
    }));
  };

  // Print test
  const printTest = (test) => {
    const printWindow = window.open('', '_blank');
    let content = `
      <html>
      <head>
        <title>${test.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; line-height: 1.6; }
          h1 { text-align: center; color: #059669; margin-bottom: 5px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .instructions { margin-bottom: 25px; padding: 10px; border-left: 3px solid #059669; background: #ecfdf5; }
          .section { margin-bottom: 25px; page-break-inside: avoid; }
          .section-title { background: #059669; color: white; padding: 8px 15px; font-weight: bold; margin-bottom: 15px; }
          .question { margin-bottom: 15px; padding: 10px; border-bottom: 1px solid #eee; }
          .q-number { font-weight: bold; color: #059669; }
          .options { margin-left: 25px; margin-top: 8px; }
          .option { margin: 5px 0; }
          .marks { float: right; color: #666; font-style: italic; }
          .answer-lines { margin-top: 10px; }
          .answer-line { border-bottom: 1px solid #ccc; height: 25px; margin: 8px 0; }
          @media print { body { padding: 20px; } .section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>${test.title}</h1>
        <p class="subtitle">${selectedYear} Economics - St Patrick's College</p>
        
        <div class="info-box">
          <div class="info-row"><span>Duration:</span><span>${test.duration}</span></div>
          <div class="info-row"><span>Total Marks:</span><span>${test.totalMarks}</span></div>
          <div class="info-row"><span>Name:</span><span>____________________</span></div>
          <div class="info-row"><span>Date:</span><span>____________________</span></div>
        </div>
        
        <div class="instructions">
          <strong>Instructions:</strong>
          <ul>${test.instructions?.map(i => `<li>${i}</li>`).join('') || ''}</ul>
        </div>
        
        ${test.sections?.map(section => `
          <div class="section">
            <div class="section-title">${section.name} (${section.marks} marks)</div>
            ${section.questions?.map(q => `
              <div class="question">
                <span class="q-number">Q${q.id}.</span> ${q.question} <span class="marks">[${q.marks} mark${q.marks > 1 ? 's' : ''}]</span>
                ${q.options ? `<div class="options">${q.options.map(opt => `<div class="option">${opt}</div>`).join('')}</div>` : ''}
                ${!q.options ? `<div class="answer-lines">${Array(Math.ceil(q.marks / 2)).fill('<div class="answer-line"></div>').join('')}</div>` : ''}
              </div>
            `).join('') || ''}
          </div>
        `).join('') || ''}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // Create assessment from curriculum
  const handleCreateAssessment = (test, term) => {
    const assessmentData = {
      fromCurriculum: true,
      title: test.title,
      subject: 'Economics',
      year: selectedYear,
      term: term,
      totalMarks: test.totalMarks,
      duration: test.duration,
      instructions: test.instructions,
      sections: test.sections,
      testId: test.id,
      autoSync: true,
      syncConfig: {
        assessmentType: 'test',
        weightPercentage: test.totalMarks >= 40 ? 20 : 15,
        maxMarks: test.totalMarks,
        markBreakdown: test.sections?.map(s => ({
          section: s.name,
          marks: s.marks,
          questionCount: s.questions?.length || 0
        })) || [],
        answerKey: extractAnswerKey(test),
        rubric: {
          totalMarks: test.totalMarks,
          sections: test.sections?.map(s => ({
            name: s.name,
            marks: s.marks,
            percentage: Math.round((s.marks / test.totalMarks) * 100)
          })) || [],
          gradeScale: {
            A: { min: 80, max: 100 },
            B: { min: 60, max: 79 },
            C: { min: 50, max: 59 },
            D: { min: 40, max: 49 },
            F: { min: 0, max: 39 }
          }
        }
      },
      createdAt: new Date().toISOString(),
      createdBy: currentUser?.name || 'Teacher'
    };

    localStorage.setItem('pendingCurriculumAssessment', JSON.stringify(assessmentData));

    toast.success('Redirecting to Grade Book to create assessment...', {
      description: `${test.title} will be added to Grade Book with auto-sync enabled.`
    });
    navigate('/dashboard/gradebook', { state: { assessmentFromCurriculum: assessmentData } });
  };

  // Extract answer key from test
  const extractAnswerKey = (test) => {
    const answerKey = {};
    test.sections?.forEach(section => {
      section.questions?.forEach(q => {
        if (q.answer) {
          answerKey[`q${q.id}`] = q.answer;
        }
      });
    });
    return answerKey;
  };

  const toggleStrandExpand = (strandId) => {
    setExpandedStrands(prev => ({
      ...prev,
      [strandId]: !prev[strandId]
    }));
  };

  const currentYearData = economicsCurriculum[selectedYear];
  const currentScheme = economicsSchemeOfWork[selectedYear];
  const currentPlanner = economicsYearlyPlanner[selectedYear];

  // Get current tests based on selected year
  const getCurrentTests = () => {
    switch(selectedYear) {
      case "Year 11": return year11EconomicsUnitTests;
      case "Year 12": return year12EconomicsUnitTests;
      case "Year 13": return year13EconomicsUnitTests;
      default: return year11EconomicsUnitTests;
    }
  };

  // Get unit tests for year (formatted for TermExamGenerator)
  const getUnitTestsForYear = () => {
    const tests = getCurrentTests();
    const allTests = [];
    ['term1', 'term2', 'term3', 'term4'].forEach(term => {
      if (tests?.[term]?.tests) {
        allTests.push(...tests[term].tests);
      }
    });
    return allTests;
  };

  // Get current exercises based on selected year
  const getCurrentExercises = () => {
    switch(selectedYear) {
      case "Year 11": return year11EconomicsExercises;
      case "Year 12": return year12EconomicsExercises;
      case "Year 13": return year13EconomicsExercises;
      default: return year11EconomicsExercises;
    }
  };

  const printSchemeOfWork = () => {
    const printWindow = window.open('', '_blank');
    const termKey = selectedTerm;
    const termData = currentScheme?.[termKey] || [];
    
    let content = `
      <html>
      <head>
        <title>Scheme of Work - ${selectedYear} Economics - ${termKey.replace('term', 'Term ')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #059669; }
          h2 { color: #374151; border-bottom: 2px solid #059669; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #059669; color: white; }
          tr:nth-child(even) { background: #f8fafc; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>St Patrick's College</h1>
        <h2>${selectedYear} Economics - Scheme of Work - ${termKey.replace('term', 'Term ')}</h2>
        <table>
          <thead>
            <tr>
              <th>Week</th>
              <th>Topic</th>
              <th>Learning Outcomes</th>
              <th>Activities</th>
            </tr>
          </thead>
          <tbody>
            ${termData.map(item => `
              <tr>
                <td>${item.week}</td>
                <td>${item.topic}</td>
                <td>${item.outcomes}</td>
                <td>${item.activities}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  return (
    <div className="space-y-6" data-testid="economics-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-emerald-600" />
            Economics Curriculum
          </h1>
          <p className="text-gray-600 mt-1">
            Vanuatu National Economics Syllabus - Years 11, 12 & 13
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select 
            value={selectedYear} 
            onValueChange={(value) => {
              setSelectedYear(value);
              setActiveTab("curriculum");
            }}
          >
            <SelectTrigger className="w-32" data-testid="economics-year-selector">
              <SelectValue>{selectedYear}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(teacherYears.length > 0 ? teacherYears : ["Year 11", "Year 12", "Year 13"]).map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-emerald-600 border-emerald-600">
            4 Strands
          </Badge>
          <Badge variant="outline" className="text-green-600 border-green-600">
            {selectedYear === "Year 11" ? "40% CA / 60% IE" : "40% IA / 60% EE"}
          </Badge>
        </div>
      </div>

      {/* Course Overview Card */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-emerald-600">4</p>
              <p className="text-sm text-gray-600">Strands</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{currentYearData?.strands?.reduce((acc, s) => acc + (s.subStrands?.length || 0), 0) || 15}+</p>
              <p className="text-sm text-gray-600">Sub-strands</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-teal-600">4</p>
              <p className="text-sm text-gray-600">Terms</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-cyan-600">
                {selectedYear === "Year 11" ? "40%" : "40%"}
              </p>
              <p className="text-sm text-gray-600">{selectedYear === "Year 11" ? "Continuous" : "Internal"}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {selectedYear === "Year 11" ? "60%" : "60%"}
              </p>
              <p className="text-sm text-gray-600">{selectedYear === "Year 11" ? "Internal Exam" : "External Exam"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 mb-6">
          <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="econ-curriculum-tab">
            <BookOpen className="w-4 h-4" />
            <span className="hidden md:inline">Curriculum</span>
          </TabsTrigger>
          <TabsTrigger value="planner" className="flex items-center gap-2" data-testid="econ-planner-tab">
            <Calendar className="w-4 h-4" />
            <span className="hidden md:inline">Planner</span>
          </TabsTrigger>
          <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="econ-schemes-tab">
            <List className="w-4 h-4" />
            <span className="hidden md:inline">Schemes</span>
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-2" data-testid="econ-resources-tab">
            <Download className="w-4 h-4" />
            <span className="hidden md:inline">Resources</span>
          </TabsTrigger>
          <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="econ-term-exam-tab">
            <FileQuestion className="w-4 h-4" />
            <span className="hidden md:inline">Term Exam</span>
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-cyan-100" data-testid="econ-quick-print-tab">
            <Files className="w-4 h-4" />
            <span className="hidden md:inline">Quick Print</span>
          </TabsTrigger>
          <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-blue-100" data-testid="econ-lesson-plans-tab">
            <PenTool className="w-4 h-4" />
            <span className="hidden md:inline">Lesson Plans</span>
          </TabsTrigger>
          <TabsTrigger value="ai-generator" className="flex items-center gap-2 bg-amber-100" data-testid="econ-ai-generator-tab">
            <Sparkles className="w-4 h-4" />
            <span className="hidden md:inline">AI Generator</span>
          </TabsTrigger>
        </TabsList>

        {/* Term Exam Tab */}
        <TabsContent value="term-exam" className="p-4" data-testid="econ-term-exam-content">
          <TermExamGenerator
            subject="Economics"
            year={parseInt(selectedYear.replace('Year ', ''))}
            unitTests={getUnitTestsForYear()}
            subjectColor="blue"
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" className="p-4" data-testid="econ-quick-print-content">
          <QuickPrintAll
            subject="Economics"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={economicsWeeklyLessons}
            subjectColor="blue"
          />
        </TabsContent>

        {/* Lesson Plans Tab - Full Week Mon-Fri */}
        <TabsContent value="lesson-plans" className="p-4" data-testid="econ-lesson-plans-content">
          <LessonPlanGenerator 
            defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
            subject="Economics"
            subjectColor="blue"
            allowedYears={['11', '12', '13']}
            weeklyLessons={economicsWeeklyLessons}
          />
        </TabsContent>

        {/* AI Lesson Plans Tab - Generate Mon-Fri lessons */}
        <TabsContent value="ai-generator" data-testid="econ-ai-generator-content">
          <AILessonPlanGenerator 
            defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
            defaultSubject="economics" 
            schoolType="secondary"
            allowedYears={teacherYears}
            allowedSubjects={['economics']}
          />
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-emerald-600" />
                  {selectedYear} Economics - Strands & Learning Outcomes
                </CardTitle>
                <CardDescription>
                  Click on each strand to view detailed learning outcomes and topics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="space-y-3">
                  {currentYearData?.strands?.map((strand, index) => {
                    const IconComponent = strandIcons[index] || TrendingUp;
                    const color = strandColors[index] || "emerald";
                    
                    return (
                      <AccordionItem key={strand.id} value={strand.id} className="border rounded-lg">
                        <AccordionTrigger className="px-4 hover:no-underline hover:bg-emerald-50">
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                              <IconComponent className={`h-5 w-5 text-${color}-600`} />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold">{strand.code}: {strand.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {strand.subStrands?.length || 0} sub-strands
                                {strand.weighting && ` • ${strand.weighting}% weighting`}
                              </p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          {/* Major Learning Outcome */}
                          {strand.majorLearningOutcome && (
                            <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                              <p className="text-sm font-medium text-emerald-700">Major Learning Outcome:</p>
                              <p className="text-sm text-gray-700">{strand.majorLearningOutcome}</p>
                            </div>
                          )}
                          
                          {/* Sub-strands */}
                          {strand.subStrands && strand.subStrands.length > 0 && (
                            <div className="mb-4">
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">Sub-strands:</h4>
                              <Accordion type="multiple" className="space-y-2">
                                {strand.subStrands.map((subStrand, idx) => (
                                  <AccordionItem key={subStrand.id} value={subStrand.id} className="border rounded-lg bg-white">
                                    <AccordionTrigger className="px-3 py-2 hover:no-underline">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-emerald-600 border-emerald-300">{subStrand.id}</Badge>
                                        <span className="font-medium text-sm">{subStrand.title}</span>
                                        <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-xs">
                                          {subStrand.slos?.length || 0} SLOs
                                        </Badge>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-3 pb-3">
                                      <p className="text-sm text-gray-600 mb-3 italic">{subStrand.description}</p>
                                      <h5 className="font-medium text-xs text-emerald-700 mb-2">Student Learning Outcomes:</h5>
                                      <ul className="space-y-1">
                                        {subStrand.slos?.map((slo, sloIdx) => (
                                          <li key={sloIdx} className="flex items-start gap-2 text-sm p-2 bg-emerald-50 rounded">
                                            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                                            <span>{slo}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </AccordionContent>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            </div>
                          )}
                          
                          {/* Topics (if available) */}
                          {strand.topics && strand.topics.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">Topics:</h4>
                              <div className="grid gap-2">
                                {strand.topics.map((topic, idx) => (
                                  <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                                    <Badge variant="outline" className="shrink-0">
                                      Week {topic.weeks}
                                    </Badge>
                                    <div>
                                      <p className="font-medium text-sm">{topic.name}</p>
                                      <p className="text-xs text-muted-foreground">{topic.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Yearly Planner Tab */}
        <TabsContent value="planner" data-testid="econ-planner-content">
          <div className="space-y-6">
            {/* Term Selector */}
            <div className="flex gap-2 flex-wrap">
              {["term1", "term2", "term3", "term4"].map((term) => (
                <Button
                  key={term}
                  variant={selectedTerm === term ? "default" : "outline"}
                  onClick={() => setSelectedTerm(term)}
                  className={selectedTerm === term ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                >
                  Term {term.replace("term", "")}
                </Button>
              ))}
            </div>

            {/* Term Overview Card */}
            {currentPlanner?.[selectedTerm] && (
              <Card>
                <CardHeader className="bg-emerald-50 border-b border-emerald-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-emerald-800">
                        Term {selectedTerm.replace("term", "")} Overview
                      </CardTitle>
                      <CardDescription className="text-emerald-700">
                        {currentPlanner[selectedTerm].weeks} • {currentPlanner[selectedTerm].focus}
                      </CardDescription>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">
                      {currentPlanner[selectedTerm].assessment}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {currentPlanner[selectedTerm].units?.map((unit, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:border-emerald-300 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-emerald-800">{unit.title}</h4>
                          <Badge variant="outline" className="text-emerald-600">{unit.weeks}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">{unit.strand}</span> - Sub-strand {unit.subStrand}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {unit.topics?.map((topic, tIdx) => (
                            <Badge key={tIdx} variant="outline" className="text-xs bg-emerald-50">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Strand Weighting Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                  Strand Weightings - {selectedYear}
                </CardTitle>
                <CardDescription>Assessment weighting distribution across strands</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentYearData?.strands?.map((strand, idx) => (
                    <div key={strand.id} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{strand.code}: {strand.name}</span>
                        <span className="text-gray-600">{strand.weighting}%</span>
                      </div>
                      <Progress value={strand.weighting * 1.67} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Schemes of Work Tab */}
        <TabsContent value="schemes" data-testid="econ-schemes-content">
          <SubjectSchemeOfWork
            subject="Economics"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={economicsWeeklyLessons}
            subjectColor="blue"
          />
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" data-testid="econ-resources-content">
          <div className="space-y-6">
            {/* Teaching Resources Grid */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Teaching Resources
                </CardTitle>
                <CardDescription>Reference materials, templates, and guides for Economics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(economicsResources || []).map((resource, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:border-emerald-300 hover:shadow-sm transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-emerald-800">{resource.title}</h4>
                        <Badge variant="outline" className="text-emerald-600 text-xs">{resource.type}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          <Badge className="bg-gray-100 text-gray-600 text-xs">{resource.unit}</Badge>
                          {resource.year && <Badge className="bg-emerald-50 text-emerald-600 text-xs">{resource.year}</Badge>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="text-emerald-600 hover:text-emerald-700">
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => {
                              setActiveTab('ai-generator');
                              toast.info(`Generate custom materials for: ${resource.title}`);
                            }}
                          >
                            <Sparkles className="w-4 h-4 mr-1" />
                            AI Generate
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Assessment Structure */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Assessment Structure - {selectedYear}
                </CardTitle>
                <CardDescription>Overview of internal and external assessment requirements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Internal Assessment / Continuous Assessment */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      {selectedYear === "Year 11" ? "Continuous Assessment (40%)" : "Internal Assessment (40%)"}
                    </h4>
                    <div className="space-y-2">
                      {selectedYear === "Year 11" ? (
                        economicsAssessment?.year11?.continuousAssessment?.components?.map((comp, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{comp.name}</span>
                            <div className="flex gap-2">
                              <Badge variant="outline">{comp.weight}%</Badge>
                              <Badge className="bg-green-100 text-green-700">{comp.count} tasks</Badge>
                            </div>
                          </div>
                        ))
                      ) : (
                        economicsAssessment?.[selectedYear.toLowerCase().replace(' ', '')]?.internalAssessment?.components?.map((comp, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{comp.name}</span>
                            <div className="flex gap-2">
                              <Badge variant="outline">{comp.weight}%</Badge>
                              <Badge className="bg-green-100 text-green-700">{comp.count} tasks</Badge>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* External / Internal Examination */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                      <ClipboardCheck className="w-4 h-4" />
                      {selectedYear === "Year 11" ? "Internal Examination (60%)" : "External Examination (60%)"}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Format:</span> {selectedYear === "Year 11" ? economicsAssessment?.year11?.internalExamination?.format : economicsAssessment?.[selectedYear.toLowerCase().replace(' ', '')]?.externalExamination?.format}</p>
                      <p><span className="font-medium">Sections:</span> {selectedYear === "Year 11" ? economicsAssessment?.year11?.internalExamination?.sections : economicsAssessment?.[selectedYear.toLowerCase().replace(' ', '')]?.externalExamination?.sections}</p>
                      <div className="mt-3">
                        <p className="font-medium mb-2">Strand Weights:</p>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span>ECO1: Market System</span>
                            <Badge>{selectedYear === "Year 13" ? "25%" : "19%"}</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>ECO2: Public Sector</span>
                            <Badge>{selectedYear === "Year 13" ? "15%" : "16%"}</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>ECO3: Money</span>
                            <Badge>{selectedYear === "Year 13" ? "16%" : "13%"}</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>ECO4: Trade</span>
                            <Badge>{selectedYear === "Year 13" ? "4%" : "12%"}</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skill Levels */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 mb-3">Assessment Skill Levels (SOLO Taxonomy)</h4>
                  <div className="grid md:grid-cols-4 gap-3">
                    {economicsAssessment?.year11?.skillLevels && Object.entries(economicsAssessment.year11.skillLevels).map(([level, description], idx) => (
                      <div key={level} className="p-3 border rounded-lg text-center">
                        <Badge className={`mb-2 ${
                          idx === 0 ? 'bg-green-100 text-green-700' :
                          idx === 1 ? 'bg-blue-100 text-blue-700' :
                          idx === 2 ? 'bg-purple-100 text-purple-700' :
                          'bg-rose-100 text-rose-700'
                        }`}>{level.charAt(0).toUpperCase() + level.slice(1)}</Badge>
                        <p className="text-xs text-gray-600">{description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
